import {
  isDemoExpired,
  type StageProjectLicenceT,
  type UserLicenceT,
} from '@/lib/licence/licence.ts'

export type LicenceBannerT =
  | { kind: 'licenceExpired' }
  | { kind: 'licenceExpiring'; expiresAt: string | null; showKeyLink: boolean }
  | {
      kind: 'demo'
      expiresAt: string | null
      issueLimit: number | null
      showCreateProject: boolean
      createProjectInWizard: boolean
    }
  | { kind: 'demoExpired'; showCreateProject: boolean; createProjectInWizard: boolean }

type ResolveLicenceBannerArgsT = {
  /** The project of the stage on screen, or null off a stage route. */
  stageProject: StageProjectLicenceT | null | undefined
  /** `licence` from /users/me: the active organization's own licence. */
  userLicence: UserLicenceT | null | undefined
  /** ADMIN/OWNER of the active organization: may enter a key and found a project. */
  isOrganizationAdmin: boolean
  /** `projectsCount` from /users/me; the demo is not counted. */
  projectsCount: number | undefined
  now: Date
}

/**
 * Which licence message the app shows (docs/plans/model-licencji.md, "Klient: co widzi
 * użytkownik"). One banner at a time, the one that explains what the user can do here:
 *
 * - on a stage, the OWNER's licence decides about writes (W1), so an expired owner licence
 *   comes first, then the demo clock;
 * - the user's own expired licence is shown only off a stage: on someone else's stage it
 *   does not block anything, and saying "you can only browse" there would be false;
 * - an expiring licence is about the user's own company and is shown everywhere, with the
 *   key entry link for whoever can use it.
 *
 * "Załóż własny projekt" is offered on the demo to every admin. It opens the `/start`
 * wizard only for a company with no project of its own: the wizard asks the server to refuse
 * a second first project (409). With projects already there, archived ones included, or with
 * an unknown count, it opens the ordinary new-project form instead (D7).
 */
export function resolveLicenceBanner({
  stageProject,
  userLicence,
  isOrganizationAdmin,
  projectsCount,
  now,
}: ResolveLicenceBannerArgsT): LicenceBannerT | null {
  const showCreateProject = isOrganizationAdmin
  const createProjectInWizard = projectsCount === 0

  if (stageProject) {
    // With the guard off for this reader (enforcement off, special user) nothing on the stage
    // is refused, so neither "you can only browse" message would be true.
    const checksApply = stageProject.licenceChecksApply === true
    if (checksApply && stageProject.ownerLicenceStatus === 'expired')
      return { kind: 'licenceExpired' }
    if (stageProject.demo) {
      const expired = isDemoExpired(stageProject.demo, now)
      if (expired && !checksApply) return null
      return expired
        ? { kind: 'demoExpired', showCreateProject, createProjectInWizard }
        : {
            kind: 'demo',
            expiresAt: stageProject.demo.expiresAt,
            issueLimit: stageProject.demo.issueLimit,
            showCreateProject,
            createProjectInWizard,
          }
    }
  }

  if (userLicence?.status === 'expired' && !stageProject) return { kind: 'licenceExpired' }
  if (userLicence?.status === 'expiring') {
    return {
      kind: 'licenceExpiring',
      expiresAt: userLicence.expiresAt,
      showKeyLink: isOrganizationAdmin,
    }
  }
  return null
}
